import { Briefcase, Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { PermissionGate } from '../../../../auth/PermissionGate';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import {
  EMPLOYMENT_TYPE_LABELS,
  type PersonnelDetail,
  type PersonnelEmploymentRecord,
} from '../personnelTypes';
import { isPostHere } from '../utils/isPostHere';

/** The unit for a post here, the employer for a previous one — never both, never twice. */
function employerOf(line: PersonnelEmploymentRecord): string | null {
  return line.orgUnitName ?? line.agencyName ?? null;
}

/**
 * Employment history — every post held HERE and every PREVIOUS employer, in one table.
 * CS Form 212 section V, and the 201 file's service record.
 *
 * **Merged 27 Oct 2026 from the Service Record and Work experience panels** (product call: the
 * two carried the same period, title and status twice, and the current post was entered once in
 * each). Two kinds of line, two write paths and two keys:
 *
 * ```
 * a post here         personnel.assign   fills a plantilla slot; ENDED, never edited or deleted
 * a previous employer personnel.update   stated text; edited, deleted and restored; always ended
 * ```
 *
 * The Create is a MENU for that reason — each entry carries its own key, so a reader holding one
 * sees one entry. Every by-row action is offered only on the kind it applies to; the server
 * refuses the other kind (409) whatever the client sends.
 *
 * **The banner states the current PRIMARY post**, reading the primary open line — never
 * `openPosts[0]`. It is gated on `plantilla.read`, as it always was: it names a unit, a title and
 * an item, all the plantilla's.
 */
export function EmploymentHistoryPanel({
  record,
  primary,
  openCount,
  onRecordPost,
  onEndPost,
  onAddPrevious,
  onEditPrevious,
  onDeletePrevious,
  onBulkDeletePrevious,
  onRestorePrevious,
}: {
  record: PersonnelDetail;
  /** The open post carrying `isPrimary`, or null when no open post does. */
  primary: PersonnelEmploymentRecord | null;
  /** How many posts are open at all — what separates "none held" from "none primary". */
  openCount: number;
  onRecordPost: () => void;
  onEndPost: (line: PersonnelEmploymentRecord) => void;
  onAddPrevious: () => void;
  onEditPrevious: (line: PersonnelEmploymentRecord) => void;
  onDeletePrevious: (line: PersonnelEmploymentRecord) => void;
  onBulkDeletePrevious: (lines: PersonnelEmploymentRecord[]) => void;
  onRestorePrevious: (line: PersonnelEmploymentRecord) => void;
}) {
  /*
    Deleted rows are hidden at rest and the cog lifts them. NOT persisted: a view that quietly
    kept showing deleted rows from yesterday is how a deleted record gets treated as live.
  */
  const [showDeleted, setShowDeleted] = useState(false);

  const lines = useMemo(
    () =>
      showDeleted ? record.employmentRecords : record.employmentRecords.filter((x) => x.isActive),
    [record.employmentRecords, showDeleted],
  );

  /*
    Searched, sorted and paged IN MEMORY: a section arrives WHOLE with the record, so the filter
    sees everything there is — the exemption from the client-side-filtering rule.
  */
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? lines.filter((x) =>
            [
              x.positionTitle,
              employerOf(x),
              x.itemNumber,
              x.salaryGrade,
              x.appointmentNatureName,
              x.remarks,
            ]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : lines;

      const field = query.sort?.replace(/^-/, '') ?? 'effectiveFrom';
      const descending = query.sort?.startsWith('-') ?? true;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'monthlySalary') {
          // A NUMBER, and rows without one sort last whichever way the column points.
          const av = a.monthlySalary ?? null;
          const bv = b.monthlySalary ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * direction;
        }

        if (field === 'isGovernmentService') {
          return (Number(a.isGovernmentService) - Number(b.isGovernmentService)) * direction;
        }

        if (field === 'effectiveTo') {
          // An open post is the MOST recent, not the least: nothing ended later than a post not yet left.
          const av = a.effectiveTo ?? null;
          const bv = b.effectiveTo ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return direction;
          if (bv === null) return -direction;
          return av.localeCompare(bv) * direction;
        }

        const pick = (row: PersonnelEmploymentRecord) =>
          field === 'positionTitle'
            ? `${row.positionTitle ?? ''} ${employerOf(row) ?? ''}`
            : field === 'itemNumber'
              ? (row.itemNumber ?? '')
              : field === 'appointmentStatus'
                ? row.appointmentStatus == null
                  ? ''
                  : EMPLOYMENT_TYPE_LABELS[row.appointmentStatus]
                : row.effectiveFrom;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [lines],
  );

  const state = useDataTable<PersonnelEmploymentRecord>({
    fetchPage,
    pageSize: 10,

    // Most recent first: a work history is read from the current post backwards.
    defaultSort: '-effectiveFrom',
  });

  const deletedReason = 'This employee is deleted. Restore them before editing the record.';

  const columns: DataColumn<PersonnelEmploymentRecord>[] = [
    {
      /*
        The ANCHOR: no single value identifies a line, so it carries TWO on one line — the
        position, then the unit (a post here) or the employer (a previous one). Exactly one of
        the two arrives, so the cell never prints a value twice.
      */
      key: 'positionTitle',
      header: 'Position — unit or employer',
      width: 300,
      sortable: true,
      render: (x) => (
        <TextCell value={[x.positionTitle, employerOf(x)].filter(Boolean).join(' — ') || null} />
      ),
    },
    {
      // A post here only. An em dash on a previous employer's line: it names no item of ours.
      key: 'itemNumber',
      header: 'Item',
      width: 120,
      sortable: true,
      render: (x) => <TextCell value={x.itemNumber ?? null} mono />,
    },
    {
      // Calendar dates, so the conversion-free formatter: a date names a day, not an instant.
      key: 'effectiveFrom',
      header: 'From',
      width: 130,
      sortable: true,
      render: (x) => <TextCell value={formatDateOnly(x.effectiveFrom)} mono />,
    },
    {
      /*
        "Present", not an em dash — an open line is the form's PRESENT, a real answer. Only a
        post here can be open; a previous employer's line always carries its end date.
      */
      key: 'effectiveTo',
      header: 'To',
      width: 130,
      sortable: true,
      render: (x) => <TextCell value={x.effectiveTo ? formatDateOnly(x.effectiveTo) : 'Present'} mono />,
    },
    {
      /*
        `mono` but NOT `numeric`: a grade reads as a code and can be one — "Band B" is a real
        answer on a previous employer's line. On a post here it is the title's grade, read live.
      */
      key: 'salaryGrade',
      header: 'SG',
      width: 90,
      render: (x) => <TextCell value={x.salaryGrade ?? null} mono />,
    },
    {
      key: 'stepIncrement',
      header: 'Step',
      width: 90,
      render: (x) => <TextCell value={x.stepIncrement ?? null} mono />,
    },
    {
      key: 'monthlySalary',
      header: 'Monthly salary',
      width: 150,
      align: 'right',
      sortable: true,
      // `formatMoney` owns two decimals and the em dash; `numeric` mutes a zero, a real salary.
      render: (x) => <TextCell value={formatMoney(x.monthlySalary)} mono numeric />,
    },
    {
      // Plain text, not a pill: a fact about an appointment, not the row's own state.
      key: 'appointmentStatus',
      header: 'Status of appointment',
      width: 170,
      sortable: true,
      render: (x) => (
        <TextCell
          value={x.appointmentStatus == null ? null : EMPLOYMENT_TYPE_LABELS[x.appointmentStatus]}
        />
      ),
    },
    {
      /*
        The form's Y/N, in the words the reader asked for. A post here is always Government — a
        plantilla item is a government post — and length of government service is counted from
        this column.
      */
      key: 'isGovernmentService',
      header: 'Government or private',
      width: 160,
      sortable: true,
      render: (x) => <TextCell value={x.isGovernmentService ? 'Government' : 'Private'} />,
    },
    {
      // A LIVE view resolved by the server. A post here only.
      key: 'appointmentNatureName',
      header: 'Nature',
      width: 170,
      render: (x) => <TextCell value={x.appointmentNatureName ?? null} />,
    },
    {
      key: 'appointmentDate',
      header: 'Appointed',
      width: 130,
      render: (x) => (
        <TextCell value={x.appointmentDate ? formatDateOnly(x.appointmentDate) : null} mono />
      ),
    },
    {
      key: 'assumptionDate',
      header: 'Assumed',
      width: 130,
      render: (x) => (
        <TextCell value={x.assumptionDate ? formatDateOnly(x.assumptionDate) : null} mono />
      ),
    },
    {
      /*
        The primary badge beside the status pill (`stage`). Read the way the server reads primary
        — open, live AND flagged — so a deleted or ended line cannot wear it.
      */
      key: 'isPrimary',
      header: 'Primary',
      width: 100,
      stage: true,
      render: (x) =>
        x.isPrimary && x.isActive && !x.effectiveTo ? (
          <StatusText tone="inactive">primary</StatusText>
        ) : null,
    },
    {
      key: 'remarks',
      header: 'Remarks',
      width: 260,
      render: (x) => <TextCell value={x.remarks ?? null} />,
    },
    {
      /*
        Status is always the last data column. Deleted is tested FIRST, then ended — a deleted
        line that read "open" would describe a post nobody holds.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        !x.isActive ? (
          <StatusText tone="inactive">deleted</StatusText>
        ) : x.effectiveTo ? (
          <StatusText tone="inactive">ended</StatusText>
        ) : (
          <StatusText tone="active">open</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <PermissionGate permission="plantilla.read">
        <PrimaryPostBanner record={record} primary={primary} openCount={openCount} />
      </PermissionGate>

      <DataTablePanel<PersonnelEmploymentRecord>
        id="personnel-employment-history"
        state={state}
        // The width of the wider of the two editors, so reading a row and correcting it match.
        rowPreview={{ title: 'Employment record', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search position, employer, item or grade"
        /*
          TWO things to create, under two keys — the panel's `createMenu`. Each entry gates on its
          own key and the button draws only while one survives, so a reader holding neither sees
          no Create at all. Disabled for STATE on a deleted employee, with the reason.
        */
        createMenu={[
          {
            key: 'record-post',
            label: 'Post at this organization',
            permission: 'personnel.assign',
            disabledReason: record.isActive ? null : deletedReason,
            onClick: onRecordPost,
          },
          {
            key: 'add-previous',
            label: 'Previous employer',
            permission: 'personnel.update',
            disabledReason: record.isActive ? null : deletedReason,
            onClick: onAddPrevious,
          },
        ]}
        /*
          The kebab offers only what applies to the KIND of line. A post here is ENDED, never
          edited or deleted — those would re-date, vacate or fill a slot. A previous employer's
          line is corrected, deleted and restored, and has nothing to end.
        */
        rowActions={(x) =>
          isPostHere(x)
            ? [
                {
                  key: `end-${x.id}`,
                  label: 'End post',
                  permission: 'personnel.assign',
                  disabledReason: !x.isActive
                    ? 'This line is deleted.'
                    : x.effectiveTo
                      ? `This post already ended on ${formatDateOnly(x.effectiveTo)}.`
                      : null,
                  onClick: () => onEndPost(x),
                },
              ]
            : [
                {
                  key: `edit-${x.id}`,
                  label: 'Edit',
                  permission: 'personnel.update',
                  disabledReason: x.isActive ? null : 'This record is deleted. Restore it before editing.',
                  onClick: () => onEditPrevious(x),
                },
                x.isActive
                  ? {
                      key: `delete-${x.id}`,
                      label: 'Delete',
                      permission: 'personnel.update',
                      destructive: true,
                      onClick: () => onDeletePrevious(x),
                    }
                  : {
                      key: `restore-${x.id}`,
                      label: 'Restore',
                      permission: 'personnel.update',
                      onClick: () => onRestorePrevious(x),
                    },
              ]
        }
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          Bulk delete reaches PREVIOUS employers' live lines only — a post here is never deleted.
          A selection holding nothing deletable says why rather than doing nothing.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive && !isPostHere(x));

          return [
            {
              key: 'delete-previous-employment',
              label: `Delete ${deletable.length} previous employment record${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? deletedReason
                : deletable.length === 0
                  ? 'Nothing selected can be deleted. A post at this organization is ended, not deleted.'
                  : null,
              onClick: () => onBulkDeletePrevious(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No employment history recorded yet."
        emptySearchMessage="No employment record matches that search."
      />
    </ProfileSection>
  );
}

/**
 * Which post this employee holds right now, in one line above the history.
 *
 * **It states a fact rather than a lifecycle, so it carries no action** — the way to change it is
 * the Create beneath it. **Four states, and all four render**: a banner that appears only when
 * something is wrong teaches the reader to skip the region.
 */
function PrimaryPostBanner({
  record,
  primary,
  openCount,
}: {
  record: PersonnelDetail;
  primary: PersonnelEmploymentRecord | null;
  openCount: number;
}) {
  // A DELETED employee holds nothing: deleting one closes every open post.
  if (!record.isActive) {
    return (
      <ProfileBanner
        id="personnel-primary-post"
        tone="slate"
        icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
        message="This employee is deleted. Every post was closed with them; restore the record before recording another."
      />
    );
  }

  if (primary) {
    return (
      <ProfileBanner
        id="personnel-primary-post"
        tone="blue"
        icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
        message={
          <>
            Currently{' '}
            <span className="font-medium">
              {primary.positionTitle} — {primary.orgUnitName}
            </span>{' '}
            on item <span className="font-mono">{primary.itemNumber}</span>, held since{' '}
            <span className="font-mono">{formatDateOnly(primary.effectiveFrom)}</span>.
            {openCount > 1
              ? ` This is their primary post of ${openCount} — their unit, their grade and their reporting line are all read from it.`
              : ''}
          </>
        }
      />
    );
  }

  // Open posts with no primary among them: the unit, grade and reporting line are unanswerable.
  if (openCount > 0) {
    return (
      <ProfileBanner
        id="personnel-primary-post"
        tone="amber"
        icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
        message={`This employee holds ${openCount} ${openCount === 1 ? 'post' : 'posts'} and none is marked primary. Their unit, grade and reporting line are read from the primary one.`}
      />
    );
  }

  return (
    <ProfileBanner
      id="personnel-primary-post"
      tone="amber"
      icon={<Briefcase className="h-4 w-4" aria-hidden="true" />}
      message="This employee holds no post here. Record one to give the record a unit, a grade and a reporting line."
    />
  );
}
